import { trim, each, get } from "lodash";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import qs from "query-string";
import Modal from "antd/lib/modal";
import Input from "antd/lib/input";
import Radio from "antd/lib/radio";
import Checkbox from "antd/lib/checkbox";
import Button from "antd/lib/button";
import notification from "@/services/notification";
import { wrap as wrapDialog, DialogPropType } from "@/components/DialogWrapper";
import { Dashboard } from "@/services/dashboard";
import { currentUser } from "@/services/auth";
import recordEvent from "@/services/recordEvent";
import url from "@/services/url";

import "./ForkDashboardDialog.less";

function collectDistinctQueries(dashboard) {
  const byId = new Map();
  each(dashboard.widgets, w => {
    if (w.visualization && w.visualization.query) {
      const q = w.visualization.query;
      const qid = Number(q.id);
      if (!byId.has(qid)) {
        byId.set(qid, { id: qid, name: q.name });
      }
    }
  });
  return Array.from(byId.values());
}

/** Same-origin absolute URL for a dashboard path, merging current query params (e.g. org_slug). */
function forkedDashboardAbsoluteUrl(dashboardPath) {
  const currentUrlParams = qs.parse(window.location.search);
  const mergedSearch = qs.stringify(currentUrlParams);
  const relative = mergedSearch ? `${dashboardPath}?${mergedSearch}` : dashboardPath;
  return url.normalize(relative);
}

function ForkDashboardDialog({ dialog, dashboard }) {
  const canForkQueries = currentUser.hasPermission("edit_query");
  const distinctQueries = useMemo(() => collectDistinctQueries(dashboard), [dashboard]);

  const [nameSuffix, setNameSuffix] = useState("");
  const [forkMode, setForkMode] = useState("dashboard");
  const [selectedQueryIds, setSelectedQueryIds] = useState(() => distinctQueries.map(q => q.id));
  const [saving, setSaving] = useState(false);

  const prevVisibleRef = useRef(false);
  // Ant Design Modal can invoke onOk with a stale handler; read latest form values at click time.
  const forkFormRef = useRef({
    nameSuffix: "",
    forkMode: "dashboard",
    selectedQueryIds: [],
    distinctQueries: [],
  });
  forkFormRef.current = { nameSuffix, forkMode, selectedQueryIds, distinctQueries };

  // Reset only when the modal opens, not when `dashboard` refreshes in the background while open
  // (otherwise fork mode and suffix reset and the API sees an empty body → "Copy of" + no query forks).
  useEffect(() => {
    const visible = dialog.props.visible;
    if (visible && !prevVisibleRef.current) {
      setNameSuffix("");
      setForkMode("dashboard");
      setSelectedQueryIds(distinctQueries.map(q => q.id));
      setSaving(false);
    }
    prevVisibleRef.current = visible;
  }, [dialog.props.visible, distinctQueries]);

  const toggleQuery = useCallback((queryId, checked) => {
    setSelectedQueryIds(prev => (checked ? [...prev, queryId] : prev.filter(id => id !== queryId)));
  }, []);

  const selectAllQueries = useCallback(() => {
    setSelectedQueryIds(distinctQueries.map(q => q.id));
  }, [distinctQueries]);

  const selectNoQueries = useCallback(() => {
    setSelectedQueryIds([]);
  }, []);

  const handleFork = useCallback(() => {
    const { nameSuffix: ns, forkMode: fm, selectedQueryIds: sel, distinctQueries: dq } = forkFormRef.current;
    const suffix = trim(ns);
    if (!suffix) {
      notification.error("Name suffix required", "Enter a suffix to append after the dashboard and query names.");
      return Promise.reject(new Error("missing suffix"));
    }

    if (fm === "queries" && sel.length === 0) {
      notification.error("No queries selected", "Select at least one query to fork, or choose dashboard only.");
      return Promise.reject(new Error("no queries"));
    }

    let forkQueriesPayload;
    if (fm === "queries") {
      forkQueriesPayload = sel.length === dq.length ? "all" : sel.map(Number);
    }

    setSaving(true);
    return Dashboard.fork({
      id: dashboard.id,
      name_suffix: suffix,
      fork_queries: forkQueriesPayload,
    })
      .then(newDashboard => {
        const absolute = forkedDashboardAbsoluteUrl(newDashboard.getUrl());
        window.open(absolute, "_blank", "noopener,noreferrer");
        recordEvent("fork", "dashboard", dashboard.id);
        dialog.close();
      })
      .catch(err => {
        const msg = get(err, "response.data.message") || err.message || "Fork failed.";
        notification.error("Could not fork dashboard", msg);
        throw err;
      })
      .finally(() => {
        setSaving(false);
      });
  }, [dashboard.id, dialog]);

  const handleCancel = useCallback(() => {
    dialog.dismiss();
  }, [dialog]);

  const suffixPreview =
    trim(nameSuffix) !== "" ? `${dashboard.name} - ${trim(nameSuffix)}` : `${dashboard.name} - …`;

  return (
    <Modal
      {...dialog.props}
      wrapClassName="fork-dashboard-modal"
      width={640}
      title="Fork dashboard"
      okText="Fork"
      cancelText="Cancel"
      onOk={handleFork}
      onCancel={handleCancel}
      okButtonProps={{
        loading: saving,
        "data-test": "ForkDashboardSubmit",
      }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      wrapProps={{ "data-test": "ForkDashboardDialog" }}>
      <p className="m-b-15">
        The new dashboard will be named <strong>{suffixPreview}</strong> (truncated if too long). Queries you fork
        will use the same pattern.
      </p>
      <div className="m-b-15">
        <label className="d-block m-b-5" htmlFor="fork-dashboard-suffix">
          Name suffix
        </label>
        <Input
          id="fork-dashboard-suffix"
          value={nameSuffix}
          onChange={e => setNameSuffix(e.target.value)}
          placeholder="e.g. staging, Q1, experiment"
          disabled={saving}
          autoFocus
        />
      </div>
      <Radio.Group
        className="m-b-10"
        value={forkMode}
        onChange={e => setForkMode(e.target.value)}
        disabled={saving}>
        <Radio value="dashboard" className="fork-dashboard-radio d-block m-b-5">
          Dashboard only — same queries and visualizations as the original
        </Radio>
        <Radio value="queries" className="fork-dashboard-radio d-block" disabled={!distinctQueries.length || !canForkQueries}>
          Fork selected queries — duplicate SQL and visualizations.
          <br />
          <span className="text-muted">Unselected queries stay linked to the originals.</span>
        </Radio>
      </Radio.Group>
      {!canForkQueries && (
        <p className="text-muted small m-b-10">You do not have permission to fork queries; dashboard-only fork is available.</p>
      )}
      {forkMode === "queries" && distinctQueries.length > 0 && (
        <div className="m-t-15">
          <div className="m-b-5">
            <span className="m-r-10">Queries on this dashboard</span>
            <Button size="small" type="link" className="p-0 m-r-10" onClick={selectAllQueries} disabled={saving}>
              Select all
            </Button>
            <Button size="small" type="link" className="p-0" onClick={selectNoQueries} disabled={saving}>
              Select none
            </Button>
          </div>
          <div className="fork-dashboard-query-list">
            {distinctQueries.map(q => (
              <div key={q.id} className="m-b-5">
                <Checkbox
                  checked={selectedQueryIds.includes(q.id)}
                  onChange={e => toggleQuery(q.id, e.target.checked)}
                  disabled={saving}>
                  {q.name} <span className="text-muted">(#{q.id})</span>
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

ForkDashboardDialog.propTypes = {
  dialog: DialogPropType.isRequired,
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default wrapDialog(ForkDashboardDialog);
